/* =============================================================================
   HK catalog + slideshow (hero section)
   - custom fade slideshow (replaces Dawn's native one: root gets .hk-no-native)
   - banner slides: product tiles, rendered by Liquid when a collection is picked,
     otherwise fetched lazily (active slide first, the rest when the browser is idle)
   - the static catalog menu (behaviour shared with the header: hk-catalog-core.js)
   Per-section values come from data attributes on #hk-catsl:
     data-autoplay="true|false"  data-speed="<ms>"  data-hk-i18n='{...}'
   ============================================================================= */
(function () {
  'use strict';
  var HK = window.HKCatalog;
  if (!HK || window.__hkHeroLoaded) return;
  window.__hkHeroLoaded = true;

  var mqDesktop = HK.mqDesktop;

  /* =========================================================
     Banner slides — product tiles
     ========================================================= */
  function colHandle(url) {
    if (!url) return null;
    try { url = new URL(url, window.location.origin).pathname; } catch (e) { /* keep as is */ }
    var m = String(url).split('?')[0].match(/\/collections\/([^\/]+)/);
    return m ? m[1] : null;
  }

  function dropPlaceholder(cell) {
    var ph = cell.querySelector('.hk-bslide__cardph');
    if (ph) ph.remove();
  }

  function idle(fn) {
    if ('requestIdleCallback' in window) window.requestIdleCallback(fn, { timeout: 4000 });
    else setTimeout(fn, 2500);
  }

  function initBanners(root) {
    var rights = Array.prototype.slice.call(root.querySelectorAll('[data-hk-bslide-collection]'));

    function load(right) {
      if (right.dataset.hkTiles === 'server') return;      /* already rendered by Liquid */
      if (right.dataset.hkTiles === 'loading' || right.dataset.hkTiles === 'done') return;
      var handle = colHandle(right.dataset.hkBslideCollection);
      var cells = Array.prototype.slice.call(right.querySelectorAll('[data-hk-bslide-cardcell]'));
      if (!handle || !cells.length) { cells.forEach(dropPlaceholder); return; }
      right.dataset.hkTiles = 'loading';

      fetch(HK.root + 'collections/' + handle + '?view=hk-banner', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.text() : Promise.reject(); })
        .then(function (txt) {
          /* alternate template not uploaded yet -> Shopify answers with the full page */
          if (/<html[\s>]/i.test(txt)) return Promise.reject();
          var tpl = document.createElement('template');
          tpl.innerHTML = txt;
          var cards = Array.prototype.slice.call(tpl.content.querySelectorAll('.hk-bslide__pcard'));
          var parentSlide = right.closest('.slideshow__slide');
          var slideHidden = !!(parentSlide && parentSlide.getAttribute('aria-hidden') === 'true');

          cells.forEach(function (cell, i) {
            dropPlaceholder(cell);
            var card = cards[i];
            if (!card) return;
            if (slideHidden) {
              /* same bookkeeping as disableFocusables(): the slideshow restores it when this slide becomes active */
              card.setAttribute('data-hk-prev-tabindex', '');
              card.setAttribute('tabindex', '-1');
            }
            cell.appendChild(card);
          });
          right.dataset.hkTiles = 'done';
        })
        .catch(function () {
          /* leave the tiles empty instead of shimmering forever; allow a retry later */
          cells.forEach(dropPlaceholder);
          right.dataset.hkTiles = '';
        });
    }

    /* everything that still needs fetching loads while the browser is idle */
    if (rights.length) idle(function () { rights.forEach(load); });

    return {
      /* make sure the tiles of this slide are (being) loaded */
      ensure: function (slide) {
        var right = slide && slide.querySelector('[data-hk-bslide-collection]');
        if (right) load(right);
      }
    };
  }

  /* =========================================================
     Slideshow
     ========================================================= */
  function initSlideshow(root, banners, cleanup) {
    var comp = root.querySelector('slideshow-component.modern-slideshow');
    var slider = root.querySelector('.hk-slider');
    if (!comp || !slider) return;

    var slides = Array.prototype.slice.call(slider.querySelectorAll('.slideshow__slide'));
    banners.ensure(slides[0]);                        /* first slide: tiles right away */
    if (slides.length < 2) return;

    root.classList.add('hk-no-native');

    var i18n = HK.readI18n(root);
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var autoplay = root.dataset.autoplay === 'true' && !reduceMotion;
    var speedMs = parseInt(root.dataset.speed, 10) || 6000;
    var dots = Array.prototype.slice.call(root.querySelectorAll('.hk-lines-under .slider-counter__link'));

    root.style.setProperty('--hk-slide-speed', speedMs + 'ms');
    /* visitor's own stop (A11Y-009): remembered across pages, shared with the logo marquee */
    var MOTION_KEY = 'hk-motion-paused';
    var userPaused = false;
    try { userPaused = localStorage.getItem(MOTION_KEY) === '1'; } catch (e) { /* storage blocked */ }

    /* an auto-rotating region must not announce every slide change */
    function setLive() { slider.setAttribute('aria-live', autoplay && !userPaused ? 'off' : 'polite'); }
    setLive();

    var index = 0;
    var focusPaused = false;

    /* ---- pause badge ---- */
    var controlsBar = root.querySelector('.slideshow__controls.hk-under');
    if (controlsBar) {
      var badge = document.createElement('div');
      badge.className = 'hk-pause-badge';
      badge.setAttribute('aria-hidden', 'true');
      badge.innerHTML = '<svg viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>';
      badge.appendChild(document.createTextNode(i18n.pause || 'Pauză'));
      controlsBar.appendChild(badge);
    }

    function restartBarAnimation() {
      dots.forEach(function (btn) {
        var dot = btn.querySelector('.dot');
        if (!dot) return;
        dot.style.animation = 'none';
        void dot.offsetHeight;
        dot.style.animation = '';
      });
    }

    function disableFocusables(slide) {
      slide.querySelectorAll('a[href], button, input, textarea, select, [tabindex]').forEach(function (el) {
        if (!el.hasAttribute('data-hk-prev-tabindex')) {
          var prev = el.getAttribute('tabindex');
          el.setAttribute('data-hk-prev-tabindex', prev == null ? '' : prev);
        }
        el.setAttribute('tabindex', '-1');
      });
    }

    function enableFocusables(slide) {
      slide.querySelectorAll('[data-hk-prev-tabindex]').forEach(function (el) {
        var prev = el.getAttribute('data-hk-prev-tabindex');
        if (prev === '') el.removeAttribute('tabindex');
        else el.setAttribute('tabindex', prev);
        el.removeAttribute('data-hk-prev-tabindex');
      });
    }

    function render(fromIndex) {
      if (fromIndex !== undefined && fromIndex !== index) {
        var leaving = slides[fromIndex];
        leaving.classList.add('is-leaving');
        leaving.setAttribute('aria-hidden', 'true');
        leaving.tabIndex = -1;
        disableFocusables(leaving);
        setTimeout(function () { leaving.classList.remove('is-leaving'); }, 750);
      }

      slides.forEach(function (s, i) {
        if (i !== index) {
          s.classList.remove('is-active');
          s.setAttribute('aria-hidden', 'true');
          s.tabIndex = -1;
          disableFocusables(s);
        }
      });

      var active = slides[index];
      active.classList.add('is-active');
      active.setAttribute('aria-hidden', 'false');
      active.tabIndex = 0;
      enableFocusables(active);
      banners.ensure(active);

      dots.forEach(function (d, i) { d.setAttribute('aria-current', i === index ? 'true' : 'false'); });
      if (autoplay) restartBarAnimation();
    }

    function go(i) {
      var from = index;
      index = (i + slides.length) % slides.length;
      render(from);
    }

    /* ---- autoplay ---- */
    var timer = null;
    function stopAutoplay() {
      if (timer) { clearInterval(timer); timer = null; }
    }
    function startAutoplay() {
      if (!autoplay || userPaused) return;
      stopAutoplay();
      timer = setInterval(function () {
        if (root.__hkBannerHovered || focusPaused || userPaused || document.hidden) return;
        go(index + 1);
      }, Math.max(2000, speedMs));
    }
    function restartAutoplay() { stopAutoplay(); startAutoplay(); }

    function onVisibility() {
      if (!autoplay) return;
      if (document.hidden) { stopAutoplay(); }
      else { restartAutoplay(); restartBarAnimation(); }
    }
    document.addEventListener('visibilitychange', onVisibility);

    /* keyboard / screen-reader users: do not rotate while focus is inside the slideshow */
    comp.addEventListener('focusin', function () { focusPaused = true; });
    comp.addEventListener('focusout', function (e) {
      if (e.relatedTarget && comp.contains(e.relatedTarget)) return;
      focusPaused = false;
      if (autoplay) { restartAutoplay(); restartBarAnimation(); }
    });

    /* ---- banner slides: pause while the pointer is over the active one ---- */
    function handleBannerEnter(e) {
      var slide = e.currentTarget;
      if (!slide.classList.contains('is-active')) return;
      root.__hkBannerHovered = true;
      root.classList.add('hk-is-paused');
    }
    function handleBannerLeave() {
      root.__hkBannerHovered = false;
      root.classList.remove('hk-is-paused');
      /* the bar starts over, so the interval must too (otherwise the slide changes mid-bar) */
      if (autoplay) { restartAutoplay(); restartBarAnimation(); }
    }
    slider.querySelectorAll('[data-hk-banner-slide]').forEach(function (bs) {
      bs.addEventListener('mouseenter', handleBannerEnter);
      bs.addEventListener('mouseleave', handleBannerLeave);
      bs.addEventListener('focusout', handleBannerLeave);
    });

    function goWithUnpause(i) {
      root.__hkBannerHovered = false;
      root.classList.remove('hk-is-paused');
      go(i);
    }

    function bindHardPress(el, fn) {
      if (!el) return;
      el.addEventListener('pointerdown', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        fn();
        restartAutoplay();
      }, true);
      el.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      }, true);
    }
    dots.forEach(function (btn, i) { bindHardPress(btn, function () { goWithUnpause(i); }); });

    /* ---- stop / start rotation button (WCAG 2.2.2) ---- */
    var toggle = root.querySelector('[data-hk-sl-toggle]');
    function applyUserPause() {
      root.classList.toggle('hk-user-paused', userPaused);
      if (toggle) toggle.setAttribute('aria-label', toggle.getAttribute(userPaused ? 'data-label-play' : 'data-label-pause'));
      setLive();
      if (userPaused) stopAutoplay();
      else { restartAutoplay(); restartBarAnimation(); }
    }
    if (toggle) {
      if (!autoplay) toggle.hidden = true;   /* rotation off in settings or reduced motion: nothing to stop */
      else {
        toggle.addEventListener('click', function () {
          userPaused = !userPaused;
          try { localStorage.setItem(MOTION_KEY, userPaused ? '1' : '0'); } catch (e) { /* storage blocked */ }
          applyUserPause();
        });
        if (userPaused) applyUserPause();
      }
    }

    /* ---- swipe + arrow keys ---- */
    var startX = null, startY = null;
    comp.addEventListener('pointerdown', function (e) {
      if (e.target.closest('.slideshow__controls, a, button, input, select, textarea')) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      startX = e.clientX;
      startY = e.clientY;
      try { comp.setPointerCapture(e.pointerId); } catch (err) { /* not capturable */ }
    });
    comp.addEventListener('pointerup', function (e) {
      if (startX == null) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      startX = null; startY = null;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy)) {
        goWithUnpause(index + (dx < 0 ? 1 : -1));
        restartAutoplay();
      }
    });
    comp.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { goWithUnpause(index - 1); restartAutoplay(); }
      if (e.key === 'ArrowRight') { goWithUnpause(index + 1); restartAutoplay(); }
    });

    go(0);
    /* first rotation only after the page has loaded, so it never competes with the initial render */
    if (document.readyState === 'complete') startAutoplay();
    else window.addEventListener('load', function () { startAutoplay(); restartBarAnimation(); }, { once: true });

    /* Stop Dawn's native slideshow controller if it also initialised on this element.
       Dawn's pause() sets aria-live="polite" on the slider, so put our value back afterwards. */
    if (typeof comp.pause === 'function') { try { comp.pause(); } catch (e) { /* ignore */ } }
    setLive();

    /* theme editor: selecting a slide block shows it and stops the rotation */
    function onBlockSelect(e) {
      if (!root.contains(e.target)) return;
      var slide = e.target.closest ? e.target.closest('.slideshow__slide') : null;
      var i = slides.indexOf(slide);
      if (i < 0) return;
      stopAutoplay();
      goWithUnpause(i);
    }
    function onBlockDeselect(e) {
      if (root.contains(e.target)) startAutoplay();
    }
    document.addEventListener('shopify:block:select', onBlockSelect);
    document.addEventListener('shopify:block:deselect', onBlockDeselect);

    cleanup.push(function () {
      stopAutoplay();
      document.removeEventListener('visibilitychange', onVisibility);
      document.removeEventListener('shopify:block:select', onBlockSelect);
      document.removeEventListener('shopify:block:deselect', onBlockDeselect);
    });
  }

  /* =========================================================
     Static catalog menu
     ========================================================= */
  function initCatalog(root, cleanup) {
    var cat = root.querySelector('.hk-catstatic[data-hk-cat]');
    if (!cat) return;

    var menu = null;
    var closer = function () { if (menu) menu.retreat(); };

    function setStage(stage) {
      cat.classList.remove('hk-stage-1', 'hk-stage-2', 'hk-stage-3', 'hk-stage-direct');
      cat.classList.add(stage === 'direct' ? 'hk-stage-direct' : 'hk-stage-' + stage);

      var open = stage === 2 || stage === 3 || stage === 'direct';
      document.documentElement.classList.toggle('hk-catsl-open', open);

      if (window.HKShadow) {
        window.HKShadow.update();
        window.HKShadow.registerCloser(closer);
      }
    }

    menu = HK.bindMenu(cat, { setStage: setStage });
    menu.reset();

    /* warm the first sub-category's cards on the first real interaction (no requests at page load) */
    ['pointerenter', 'focusin', 'touchstart'].forEach(function (ev) {
      cat.addEventListener(ev, menu.warm, { once: true, passive: true });
    });

    cat.addEventListener('mouseleave', function (e) {
      if (!HK.isHoverDesktop()) return;
      var panel = cat.querySelector('.hk-catstatic__panel');
      if (panel && e.relatedTarget && panel.contains(e.relatedTarget)) return;
      menu.retreat();
    });
    var panel = cat.querySelector('.hk-catstatic__panel');
    if (panel) {
      panel.addEventListener('mouseleave', function (e) {
        if (!HK.isHoverDesktop()) return;
        if (cat.contains(e.relatedTarget)) return;
        menu.retreat();
      });
    }

    /* desktop: collapse when keyboard focus leaves the catalog (mouse users are handled by mouseleave) */
    cat.addEventListener('focusout', function (e) {
      if (!mqDesktop.matches) return;
      if (e.relatedTarget && cat.contains(e.relatedTarget)) return;
      if (cat.matches(':hover')) return;
      if (menu.state.stage === 1) return;
      menu.retreat();
    });

    /* Escape collapses the catalog and puts focus back on the category that was open */
    cat.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' || menu.state.stage === 1) return;
      var l1Key = menu.state.l1;
      var l1Btn = l1Key ? cat.querySelector('[data-hk-l1-btn][data-hk-l1="' + l1Key + '"]') : null;
      var hadFocus = cat.contains(document.activeElement);
      menu.retreat();
      if (l1Btn && hadFocus) menu.withoutFocusNav(function () { l1Btn.focus({ preventScroll: true }); });
    });

    cleanup.push(function () {
      document.documentElement.classList.remove('hk-catsl-open');
    });
  }

  /* =========================================================
     Bottom tabs — mark the one that matches the current URL
     ========================================================= */
  function initTabs(root) {
    root.querySelectorAll('.hk-tab').forEach(function (tab) {
      tab.classList.remove('is-active');
      var href = tab.getAttribute('href');
      if (!href || href === '#') return;
      try {
        var tabPath = new URL(href, window.location.origin).pathname;
        if (tabPath === window.location.pathname) tab.classList.add('is-active');
      } catch (e) { /* ignore bad hrefs */ }
    });
  }

  /* =========================================================
     Boot
     ========================================================= */
  function initHero(root) {
    if (root.dataset.hkInit === '1') return;
    root.dataset.hkInit = '1';
    var cleanup = [];
    var banners = initBanners(root);
    initSlideshow(root, banners, cleanup);
    initCatalog(root, cleanup);
    initTabs(root);
    root.__hkDestroy = function () { cleanup.forEach(function (fn) { fn(); }); };
  }

  function initAll() {
    document.querySelectorAll('.hk-catsl[data-hk-catsl]').forEach(initHero);
  }

  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:section:unload', function (e) {
    var t = e.target;
    var el = !t || !t.querySelector ? null : (t.classList && t.classList.contains('hk-catsl') ? t : t.querySelector('.hk-catsl'));
    if (el && el.__hkDestroy) el.__hkDestroy();
  });

  initAll();
})();